<template>
  <div>
    <b-card
      no-body
      class="card-company-table"
    >
      <b-card-header>
        <b-card-title>{{ $t('Balance Sheet Snapshot') }}</b-card-title>
        <feather-icon
          icon="RotateCwIcon"
          class="cursor-pointer"
          @click="fetchBalanceSheet"
        />
      </b-card-header>

      <b-table
        ref="balanceSheetRef"
        :items="balanceSheetList"
        :busy="isLoadingBalanceSheet"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>
      </b-table>
    </b-card>

    <b-card
      no-body
      class="card-company-table"
    >
      <b-card-header>
        <b-card-title>{{ $t('Income Statement Snapshot') }}</b-card-title>
        <feather-icon
          icon="RotateCwIcon"
          class="cursor-pointer"
          @click="fetchIncomeStatement"
        />
      </b-card-header>
      <b-table
        ref="incomeStatementRef"
        :items="incomeStatementList"
        :busy="isLoadingIncomeStatement"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>
      </b-table>
    </b-card>
  </div>

</template>

<script>
import {
  BCard, BTable, BCardHeader, BCardTitle, BSpinner,
} from 'bootstrap-vue'
import { onMounted, computed, ref } from '@vue/composition-api'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { useActions } from 'vuex-composition-helpers'
import useToastAlert from '../../../composable/useToast'
import { toBalance } from '@/helpers/formatAmount'

export default {
  components: {
    BCard,
    BTable,
    BCardHeader,
    BCardTitle,
    BSpinner,
  },

  setup() {
    const { getBalanceSheetReport, getProfitOrLoss } = useActions({
      getBalanceSheetReport: 'reports/getBalanceSheetReport',
      getProfitOrLoss: 'reports/getProfitOrLoss',
    })
    const { t } = useI18nUtils()
    const { toastAlert } = useToastAlert()
    const balanceSheetList = ref([])
    const incomeStatementList = ref([])
    const tableHeaders = ref([
      { key: 'category', label: 'Categories', thStyle: { width: '70%' } },
      { key: 'amount', label: 'Amount' },
    ])

    const translatedTableHeaders = computed(() => tableHeaders.value.map(header => ({ ...header, label: header.label ? t(header.label) : header.key })))
    const isLoadingBalanceSheet = ref(false)
    const isLoadingIncomeStatement = ref(false)

    const fetchBalanceSheet = async () => {
      try {
        isLoadingBalanceSheet.value = true
        const response = await getBalanceSheetReport({
          level: 1,
          isCurrentFinancialDate: true,
          branchCode: '',
          financialDate: '',
        })

        const { status, data } = response
        if (status === 200 && data) {
          // eslint-disable-next-line array-callback-return, consistent-return
          balanceSheetList.value = data.data.map(item => {
            if (!item.category.toLowerCase().includes('total') || item.category.toLowerCase().includes('total liabilities & capital')) {
              return {
                ...item,
                amount: item.category.toLowerCase().includes('total liabilities & capital') ? toBalance(item.total) : toBalance(item.amount),
              }
            }
          }).filter(item => item !== undefined)
        }
      } catch (err) {
        toastAlert(err)
      } finally {
        isLoadingBalanceSheet.value = false
      }
    }

    const fetchIncomeStatement = async () => {
      try {
        isLoadingIncomeStatement.value = true
        const response = await getProfitOrLoss({
          level: 1,
          isCurrentFinancialDate: true,
          branchCode: '',
          financialDateFrom: '',
          financialDateTo: '',
        })

        const { status, data } = response
        if (status === 200 && data) {
          // eslint-disable-next-line array-callback-return, consistent-return
          incomeStatementList.value = data.data.map(item => {
            if (!item.category.toLowerCase().includes('total') || item.category.toLowerCase().includes('profit')) {
              return {
                ...item,
                amount: item.category.toLowerCase().includes('profit') ? toBalance(item.totalAmount) : toBalance(item.amount),
              }
            }
          }).filter(item => item !== undefined)
        }
      } catch (err) {
        toastAlert(err)
      } finally {
        isLoadingIncomeStatement.value = false
      }
    }

    onMounted(() => {
      fetchBalanceSheet()
      fetchIncomeStatement()
    })

    return {
      balanceSheetList,
      incomeStatementList,
      tableHeaders,
      isLoadingBalanceSheet,
      isLoadingIncomeStatement,
      fetchBalanceSheet,
      fetchIncomeStatement,
      translatedTableHeaders,
    }
  },
}
</script>

  <style lang="scss" scoped>
  @import '~@core/scss/base/bootstrap-extended/include';
  @import '~@core/scss/base/components/variables-dark';

  .card-company-table ::v-deep td .b-avatar.badge-light-company {
    .dark-layout & {
      background: $theme-dark-body-bg !important;
    }
  }
  </style>
